import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { HiX, HiArrowRight, HiShoppingBag } from 'react-icons/hi';
import {
  trackAddToCart as gaAddToCart,
  trackPlanetShopClick,
  trackSelectItem as gaSelectItem,
  trackViewItem as gaViewItem,
} from '../utils/analytics';
import { getProducts } from '../backend/utils/orderUtils';
import { mapDbProduct } from '../utils/productMap';
import { useCart } from '../context/CartContext';
import useScrollLock from '../utils/useScrollLock';
import ProductModal from './ProductModal';
import '../styles/PlanetModal.css';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

/* Enough to be worth swiping, few enough that the modal doesn't turn
   into the shop page. */
const HOW_MANY_PICKS = 8;

const PlanetModal = ({ planet, onClose }) => {
  const navigate = useNavigate();
  const { addItem } = useCart();

  const [picks, setPicks] = useState([]);
  const [loadingPicks, setLoadingPicks] = useState(true);
  const [added, setAdded] = useState(null);
  const [openProduct, setOpenProduct] = useState(null);

  const planetName = planet?.name;

  useScrollLock(!!planet);

  // Escape closes, as it does in every other dialog on the site.
  useEffect(() => {
    if (!planet) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [planet, onClose]);

  /* What's actually on the shelf for this planet. Hooks have to run on
     every render, so this sits above the `!planet` guard below and
     bails out inside instead. */
  useEffect(() => {
    if (!planetName) return undefined;

    let live = true;
    setLoadingPicks(true);

    getProducts({ planet: planetName, inStock: true }).then(
      ({ success, products }) => {
        if (!live) return;
        setPicks(
          success ? products.slice(0, HOW_MANY_PICKS).map(mapDbProduct) : []
        );
        setLoadingPicks(false);
      }
    );

    // The modal can be closed before the catalogue answers.
    return () => {
      live = false;
    };
  }, [planetName]);

  useEffect(() => {
    if (!added) return undefined;
    const timer = setTimeout(() => setAdded(null), 1800);
    return () => clearTimeout(timer);
  }, [added]);

  if (!planet) return null;

  const stone = planet.bestStone || {};

  const addToBag = (product) => {
    addItem(product, 1);
    setAdded(product.id);
    gaAddToCart(product, 1, 'Planet Modal');
  };

  /* The tile opens the full product on top of this modal rather than
     navigating away — close it and you are back on the planet card you
     were reading. */
  const openDetails = (product) => {
    setOpenProduct(product);
    gaSelectItem(product, 'Planet Modal');
    gaViewItem(product);
  };

  const goToShop = () => {
    trackPlanetShopClick(planet.name);
    onClose();
    navigate(`/shop?planet=${encodeURIComponent(planet.name)}`);
  };

  return (
    <AnimatePresence>
      <motion.div
        className="planet-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="planet-modal-content"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: 'spring', damping: 25 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button className="planet-modal-close" onClick={onClose}>
            <HiX />
          </button>

          {/* Everything scrollable lives in here; the CTA below stays put */}
          <div className="planet-modal-scroll">

          {/* Header */}
          <div className="planet-modal-header">
            <div
              className="planet-modal-icon"
              style={{ backgroundColor: planet.color || '#E8E3D8' }}
            >
              {planet.image ? (
                <img
                  src={planet.image}
                  alt={planet.name}
                  className="planet-modal-photo"
                  draggable={false}
                />
              ) : (
                <span className="planet-modal-emoji">{planet.symbol}</span>
              )}
            </div>
            <h2 className="planet-modal-title">{planet.name}</h2>
            <p className="planet-modal-subtitle">RECOMMENDED</p>
          </div>

          {/* Body */}
          <div className="planet-modal-body">
            {/* Info Grid */}
            <div className="planet-info-grid">
              <div className="info-item">
                <span className="info-label">Element</span>
                <span className="info-value">{planet.element || 'Fire'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Day</span>
                <span className="info-value">{planet.day || 'Sunday'}</span>
              </div>
            </div>

            {/* Trust Badges */}
            {/* <div className="trust-badges">
              <div className="trust-badge">
                <div className="trust-badge-icon">
                  <HiShieldCheck />
                </div>
                <span>IGI Certified</span>
              </div>
              <div className="trust-badge">
                <div className="trust-badge-icon">
                  <HiTruck />
                </div>
                <span>Fast Delivery</span>
              </div>
              <div className="trust-badge">
                <div className="trust-badge-icon">
                  <HiChatAlt />
                </div>
                <span>Free Consultation</span>
              </div>
            </div> */}

            {/* Properties — the heading sits inline with the pills now
                rather than on its own centred line, which is most of
                where the space went. */}
            <div className="planet-properties">
              <h4>Key Properties</h4>
              <div className="properties-tags">
                {planet.properties?.map((prop, idx) => (
                  <span key={idx} className="property-tag">{prop}</span>
                )) || (
                  <>
                    <span className="property-tag">Grounding</span>
                    <span className="property-tag">Protection</span>
                    <span className="property-tag">Clarity</span>
                    <span className="property-tag">Transformation</span>
                  </>
                )}
              </div>
            </div>

            {/* What's in stock for this planet, right where someone has
                just read why they want it. */}
            {(loadingPicks || picks.length > 0) && (
              <div className="planet-picks">
                <h4>Shop {planet.name} Stones</h4>

                <div className="picks-rail">
                  {loadingPicks
                    ? [0, 1, 2].map((i) => (
                        <div className="pick pick-skeleton" key={i} />
                      ))
                    : picks.map((product) => (
                        <div className="pick" key={product.id}>
                          {/* Everything above the button opens the
                              product; only the button adds it. */}
                          <div
                            className="pick-tap"
                            role="button"
                            tabIndex={0}
                            onClick={() => openDetails(product)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openDetails(product);
                              }
                            }}
                            aria-label={`View ${product.name}`}
                          >
                            <div className="pick-img">
                              {product.image ? (
                                <img
                                  src={product.image}
                                  alt={product.name}
                                  loading="lazy"
                                />
                              ) : (
                                <span>{product.emoji || '💎'}</span>
                              )}
                            </div>

                            <span className="pick-name">{product.name}</span>
                            <span className="pick-price">
                              {inr(product.price)}
                            </span>
                          </div>

                          <button
                            className={`pick-btn ${
                              added === product.id ? 'is-added' : ''
                            }`}
                            onClick={() => addToBag(product)}
                          >
                            {added === product.id ? (
                              'Added ✓'
                            ) : (
                              <>
                                <HiShoppingBag />
                                <span>Add to Bag</span>
                              </>
                            )}
                          </button>
                        </div>
                      ))}
                </div>
              </div>
            )}

            {/* Best Stone Section */}
            <div className="best-stone-section">
              <div className="best-stone-header">
                <h3>Best Stone for {planet.name}</h3>
                <span className="best-stone-badge">RECOMMENDED</span>
              </div>

              <div className="stone-card">
                {/* Stone Image */}
                <div className="stone-image">
                  {stone.image ? (
                    <img src={stone.image} alt={stone.name} />
                  ) : (
                    <div className="stone-image-fallback">
                      {stone.emoji || '💎'}
                    </div>
                  )}
                </div>

                {/* Stone Name */}
                <h4 className="stone-name">{stone.name}</h4>

                {stone.description && (
                  <p className="stone-description">{stone.description}</p>
                )}

                {/* Stone Properties */}
                {stone.properties?.length > 0 && (
                  <div className="stone-properties">
                    <h5>Stone Properties</h5>
                    <div className="properties-tags">
                      {stone.properties.map((prop, idx) => (
                        <span key={idx} className="property-tag">
                          {prop}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Benefits */}
                {stone.benefits?.length > 0 && (
                  <div className="stone-benefits">
                    <h5>Benefits</h5>
                    <ul>
                      {stone.benefits.map((benefit, idx) => (
                        <li key={idx}>{benefit}</li>
                      ))}
                    </ul>
                  </div>
                )}

              </div>
            </div>
          </div>

          </div>{/* /planet-modal-scroll */}

          {/* CTA — jump to the shop pre-filtered to this planet. Pinned
              here rather than at the end of the content, which took a
              lot of scrolling to reach. */}
          <div className="planet-modal-footer">
            <button className="shop-stone-btn" onClick={goToShop}>
              <span>Explore {planet.name} Stones</span>
              <HiArrowRight />
            </button>
          </div>
        </motion.div>
      </motion.div>

      {/* Opened from a tile in the rail above, stacked over this modal.
          Only mounted when there is something to show — it holds a
          scroll lock for as long as it exists. */}
      {openProduct && (
        <ProductModal
          product={openProduct}
          onAddToCart={addToBag}
          onClose={() => setOpenProduct(null)}
        />
      )}
    </AnimatePresence>
  );
};

export default PlanetModal;